import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronDown, X, Calendar, Download, Eye, Clock, CheckCircle, AlertCircle, Truck, Tag, Package, Shield, Edit, FileX, FileCheck, Upload, FileText } from 'lucide-react';
import BusinessUnitName from '../../components/BusinessUnitName';
import BolUploadModal from '../../components/BolUploadModal';
import BolViewModal from '../../components/BolViewModal';
import OutboundRecordViewModal from '../../components/OutboundRecordViewModal';
import LogiwaSyncBadge from '../components/LogiwaSyncBadge';
import LogiwaPushSection from '../components/LogiwaPushSection';
import businessUnitService from '../../../../../services/businessUnitService';
import { formatDate, formatTime, formatDateTime } from '../../components/utils/dateUtils';
import { useTimezone } from '../../../../../hooks/useTimezone';
import useServerTruckSearch from '../../hooks/useServerTruckSearch';
import TruckNotesThread from '../../components/TruckNotesThread';

const HistoryView = ({ truckData, openTruckEditor }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ 
    start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0], 
    end: new Date().toISOString().split('T')[0] 
  });
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [showBolUploadModal, setShowBolUploadModal] = useState(false);
  const [bolUploadTruck, setBolUploadTruck] = useState(null);
  const [showBolViewModal, setShowBolViewModal] = useState(false);
  const [bolViewTruck, setBolViewTruck] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  
  // Get user's timezone
  const userTimezone = useTimezone();
  
  // Load business units on component mount
  useEffect(() => {
    const loadBusinessUnits = async () => {
      try {
        const units = await businessUnitService.getAllBusinessUnits();
        setBusinessUnits(units);
      } catch (error) {
        console.error('Error loading business units:', error);
      }
    };
    loadBusinessUnits();
  }, []);
  
  // Helper function for robust/fuzzy string matching
  const fuzzyMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return false;
    
    // Convert to lowercase for case-insensitive matching
    const normalizedText = text.toString().toLowerCase();
    const normalizedSearch = searchTerm.toLowerCase();
    
    // Direct substring match
    if (normalizedText.includes(normalizedSearch)) return true;
    
    // Check if all characters of search term appear in order in the text
    let searchIndex = 0;
    for (let i = 0; i < normalizedText.length && searchIndex < normalizedSearch.length; i++) {
      if (normalizedText[i] === normalizedSearch[searchIndex]) {
        searchIndex++;
      }
    }
    return searchIndex === normalizedSearch.length;
  };
  
  // Filter history trucks - outbound specific
  const historyTrucks = truckData.filter(truck => {
    // For outbound, a truck is in history if it has status 'Completed', 'Shipped', or 'Cancelled'
    return truck.status === 'Completed' || truck.status === 'Shipped' || truck.status === 'Cancelled';
  });

  const filteredTrucks = historyTrucks.filter(truck => {
    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'conforming' && !truck.conforming) return false;
      if (statusFilter === 'non-conforming' && truck.conforming) return false;
    }
    
    // Date range filter
    if (dateRange.start || dateRange.end) {
      // Get the UTC date from truck.created_at for filtering
      const createdDate = new Date(truck.created_at);
      
      // Extract the local date parts (year, month, day) to compare dates without time
      const createdYear = createdDate.getFullYear();
      const createdMonth = createdDate.getMonth() + 1; // JavaScript months are 0-based
      const createdDay = createdDate.getDate();
      
      // Format as YYYY-MM-DD for string comparison
      const createdDateStr = `${createdYear}-${createdMonth.toString().padStart(2, '0')}-${createdDay.toString().padStart(2, '0')}`;
      
      if (dateRange.start && createdDateStr < dateRange.start) return false;
      if (dateRange.end && createdDateStr > dateRange.end) return false;
    }
    
    // Apply search filter — match the same field set the server's search param covers
    // so client-side filter and server fallback stay in sync.
    if (searchTerm) {
      const businessUnit = businessUnits.find(unit =>
        String(unit.id) === String(truck.business_unit)
      );
      const fields = [
        truck.carrier,
        truck.customer,
        truck.po,
        truck.order_number,
        truck.destination,
        truck.conf_number,
        truck.scheduler_notes,
        truck.shipper_notes,
        businessUnit && businessUnit.name,
      ];
      if (!fields.some((f) => f && fuzzyMatch(String(f), searchTerm))) {
        return false;
      }
    }

    return true;
  });

  // Server-side fallback: when an active search term is set, hit the server so we can find
  // history that aged out of BaseDashboard's 90-day buffer. We merge by id, dedup, then re-sort.
  const { results: serverResults, status: serverSearchStatus } = useServerTruckSearch({
    mode: 'outbound',
    searchTerm,
    extraParams: {}, // No status filter — server returns all matches; we narrow client-side below.
    enabled: !!searchTerm && searchTerm.trim().length >= 2,
  });

  const mergedTrucks = (() => {
    if (!searchTerm || serverSearchStatus !== 'ready') return filteredTrucks;
    const seen = new Set(filteredTrucks.map((t) => t.id));
    const completedStatuses = new Set(['Completed', 'Shipped', 'Cancelled']);
    const extras = serverResults.filter(
      (t) => completedStatuses.has(t.status) && !seen.has(t.id)
    );
    return filteredTrucks.concat(extras);
  })();

  // Sort trucks by completion date (newest first)
  const sortedTrucks = [...mergedTrucks].sort((a, b) => {
    // Use shipped_at for shipped/completed trucks, or completed_at as fallback, or created_at as final fallback
    const aDate = a.shipped_at || a.completed_at || a.created_at;
    const bDate = b.shipped_at || b.completed_at || b.created_at;
    return new Date(bDate) - new Date(aDate);
  });

  // The formatting functions are now imported from dateUtils
  
  // Get conforming badge info
  const getConformingBadge = (conforming) => {
    if (conforming) {
      return { 
        color: 'bg-green-600 text-white',
        icon: <CheckCircle className="w-4 h-4 mr-1" />,
        label: 'Conforming'
      };
    } else {
      return { 
        color: 'bg-red-600 text-white',
        icon: <AlertCircle className="w-4 h-4 mr-1" />,
        label: 'Non-Conforming'
      };
    }
  };
  
  // Handle BOL upload
  const handleBolUpload = async (truckId, file, bolNumber) => {
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('bolFile', file);
      const dataPayload = {
        completed_at: new Date().toISOString(),
      };
      if (bolNumber) dataPayload.bol_number = bolNumber;
      formData.append('data', JSON.stringify(dataPayload));

      // Use the existing complete-with-bol endpoint
      const response = await fetch(`/api/outbound-trucks/${truckId}/complete-with-bol`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to upload BOL');
      }

      // Success - refresh truck data by triggering parent update
      // Since we don't have direct access to refresh, we'll need to close modal
      // and rely on WebSocket updates or manual refresh
      alert('BOL uploaded successfully!');
      
      // Update the local truck data to reflect BOL upload
      const updatedTruck = sortedTrucks.find(t => t.id === truckId);
      if (updatedTruck) {
        updatedTruck.bol_file_path = 'uploaded'; // Temporary indicator
      }
      
      return true;
    } catch (error) {
      console.error('Error uploading BOL:', error);
      throw error;
    }
  };

  // Open BOL upload modal
  const openBolUploadModal = (truck) => {
    setBolUploadTruck(truck);
    setShowBolUploadModal(true);
  };
  
  // Open BOL view modal
  const openBolViewModal = (truck) => {
    setBolViewTruck(truck);
    setShowBolViewModal(true);
  };
  
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Search and filters - fixed area */}
      <div className="px-6 pt-4 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-2">
          <div className="flex-1 min-w-[280px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search carrier, customer, PO, order #, conf #, destination, notes…"
                className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-md text-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <div className="absolute -bottom-5 left-1 text-xs text-slate-500">
                  {serverSearchStatus === 'loading' && 'Searching all history…'}
                  {serverSearchStatus === 'ready' && (
                    <>Found {sortedTrucks.length} match{sortedTrucks.length === 1 ? '' : 'es'} (incl. older history).</>
                  )}
                  {serverSearchStatus === 'error' && 'Server search failed; showing buffered matches only.'}
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md"
              onClick={() => setFilterOpen(!filterOpen)}
            >
              <Filter size={16} />
              Filters
              <ChevronDown size={16} className={`transform transition-transform ${filterOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
              <Download size={16} />
              Export
            </button>
          </div>
        </div>
        
        {/* Expanded filters */}
        {filterOpen && (
          <div className="mb-4 p-4 bg-slate-700 rounded-md">
            <div className="flex flex-wrap gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Status</label>
                <select 
                  className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Statuses</option>
                  <option value="conforming">Conforming</option>
                  <option value="non-conforming">Non-Conforming</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Date Range</label>
                <div className="flex items-center gap-2">
                  <input 
                    type="date" 
                    className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                  />
                  <span className="text-slate-400">to</span>
                  <input 
                    type="date" 
                    className="bg-slate-800 border border-slate-600 text-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex items-end">
                <button 
                  className="flex items-center gap-1 px-3 py-2 bg-slate-600 hover:bg-slate-500 text-slate-200 rounded-md"
                  onClick={() => {
                    setStatusFilter('all');
                    setDateRange({ 
                      start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0], 
                      end: new Date().toISOString().split('T')[0] 
                    });
                    setSearchTerm('');
                  }}
                >
                  <X size={16} />
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Main content - scrollable */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden px-6 pb-6">
        {/* Truck history list - scrollable */}
        <div className={`${selectedTruck ? 'w-full lg:w-1/2 pr-0 lg:pr-3' : 'w-full'} overflow-hidden flex flex-col h-full`}>
          <h3 className="text-lg font-medium text-slate-200 mb-4">
            Outbound Delivery History ({sortedTrucks.length})
          </h3>
          
          <div className="flex-1 overflow-y-auto pr-1">
            {sortedTrucks.length === 0 ? (
              <div className="bg-slate-800 rounded-lg p-8 text-center">
                <Calendar className="w-12 h-12 mx-auto text-slate-500 mb-3" />
                <p className="text-slate-400">No historical records match your current filters</p>
                {(statusFilter !== 'all' || dateRange.start || dateRange.end || searchTerm) && (
                  <button 
                    className="mt-3 px-3 py-1 bg-slate-700 text-slate-300 rounded hover:bg-slate-600"
                    onClick={() => {
                      setStatusFilter('all');
                      setDateRange({ 
                        start: new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString().split('T')[0], 
                        end: new Date().toISOString().split('T')[0] 
                      });
                      setSearchTerm('');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {sortedTrucks.map(truck => (
                  <div 
                    key={truck.id}
                    className={`bg-slate-800 rounded-lg p-4 hover:bg-slate-750 cursor-pointer ${
                      selectedTruck && selectedTruck.id === truck.id ? 'ring-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setSelectedTruck(truck)}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-200">{truck.carrier}</h4>
                        <div className="flex items-center text-sm text-slate-400 mt-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(truck.shipped_at)}
                        </div>
                        {/* BOL Status Indicator */}
                        {truck.status === 'Completed' && (
                          <div className={`flex items-center text-xs mt-2 ${
                            truck.bol_file_path
                              ? 'text-green-400'
                              : 'text-amber-400'
                          }`}>
                            {truck.bol_file_path ? (
                              <>
                                <FileCheck className="w-3.5 h-3.5 mr-1" />
                                <span>BOL Uploaded</span>
                              </>
                            ) : (
                              <>
                                <FileX className="w-3.5 h-3.5 mr-1" />
                                <span className="font-medium">No BOL Uploaded</span>
                              </>
                            )}
                          </div>
                        )}
                        {/* Logiwa sync badge — renders only if a join row exists */}
                        <div className="mt-2">
                          <LogiwaSyncBadge truckId={truck.id} />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {openTruckEditor && (
                          <button 
                            className="text-amber-400 hover:text-amber-300 flex items-center text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTruckEditor(truck);
                            }}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </button>
                        )}
                        <div className={`rounded-full px-3 py-1 text-xs flex items-center ${getConformingBadge(truck.conforming).color}`}>
                          {getConformingBadge(truck.conforming).icon}
                          <span>{getConformingBadge(truck.conforming).label}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">Business Unit</p>
                        <p className="text-slate-300"><BusinessUnitName id={truck.business_unit} /></p>
                      </div>
                      {truck.customer && (
                        <div>
                          <p className="text-xs text-slate-400">Customer</p>
                          <p className="text-slate-300">{truck.customer}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs text-slate-400">Scheduled/Departed</p>
                        <p className="text-slate-300">{formatTime(truck.scheduled_at, 'h:mm a', userTimezone)} / {formatTime(truck.shipped_at, 'h:mm a', userTimezone)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Load Material</p>
                        <p className="text-slate-300">{truck.load_material || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Size</p>
                        <p className="text-slate-300">
                          {truck.size} {truck.size === 1 ? 'pallet' : 'pallets'}
                        </p>
                      </div>
                    </div>
                    
                    {!truck.conforming && (
                      <div className="mt-3 text-xs bg-red-900/30 border border-red-800 rounded p-2 text-red-200">
                        Non-conforming — open record for shipper notes.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Detail view (appears when a truck is selected) */}
        {selectedTruck && (
          <div className="w-full lg:w-1/2 h-full mt-4 lg:mt-0 flex flex-col overflow-hidden">
            <div className="bg-slate-700 rounded-lg p-4 h-full flex flex-col overflow-hidden">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-slate-200 flex items-center">
                  <Clock className="w-5 h-5 mr-2" />
                  Shipment Record
                </h3>
                <button
                  className="text-slate-400 hover:text-slate-300"
                  onClick={() => setSelectedTruck(null)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Scrollable content area */}
              <div className="flex-1 overflow-y-auto pr-1">
                {/* Header info */}
                <div className="bg-slate-800 rounded-md p-4 mb-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="text-lg font-medium text-slate-200">{selectedTruck.carrier}</h4>
                      <p className="text-sm text-slate-400">
                        <BusinessUnitName id={selectedTruck.business_unit} />
                        {selectedTruck.customer && ` • Customer: ${selectedTruck.customer}`}
                      </p>
                    </div>
                    <div className={`rounded-full px-3 py-1 text-xs flex items-center ${getConformingBadge(selectedTruck.conforming).color}`}>
                      {getConformingBadge(selectedTruck.conforming).icon}
                      <span>{getConformingBadge(selectedTruck.conforming).label}</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <p className="text-xs text-slate-400">Scheduled Date</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    {selectedTruck.dock_door && (
                      <div>
                        <p className="text-xs text-slate-400">Dock</p>
                        <p className="text-sm text-slate-300">{selectedTruck.dock_door}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-slate-400">Staging Start</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.staged_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Loading Complete</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.loaded_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Departure Date/Time</p>
                      <p className="text-sm text-slate-300">{formatDateTime(selectedTruck.shipped_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Load Type</p>
                      <p className="text-sm text-slate-300">{selectedTruck.load_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Load Material</p>
                      <p className="text-sm text-slate-300">{selectedTruck.load_material || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Size</p>
                      <p className="text-sm text-slate-300">{selectedTruck.size} {selectedTruck.size === 1 ? 'pallet' : 'pallets'}</p>
                    </div>
                    {selectedTruck.secure && (
                      <div className="col-span-2">
                        <p className="text-xs flex items-center text-amber-400">
                          <Shield className="w-4 h-4 mr-1" />
                          This shipment required secure handling procedures
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Notes (read-only thread) */}
                <div className="bg-slate-800 rounded-md p-4 mb-4">
                  <h4 className="font-medium text-slate-200 mb-2">Notes</h4>
                  <TruckNotesThread
                    truckType="outbound"
                    truckId={selectedTruck.id}
                    mode="read"
                    theme="dark"
                    emptyMessage="No notes on this truck."
                  />
                </div>
                
                {/* BOL Status */}
                {selectedTruck.status === 'Completed' && (
                  <div className="bg-slate-800 rounded-md p-4 mb-4">
                    <h4 className="font-medium text-slate-200 mb-2">Bill of Lading (BOL)</h4>
                    {selectedTruck.bol_file_path ? (
                      <div>
                        <div className="flex items-center text-green-400 mb-3">
                          <FileCheck className="w-5 h-5 mr-2" />
                          <span className="text-sm">BOL has been uploaded</span>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                            onClick={() => openBolViewModal(selectedTruck)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </button>
                          <button 
                            className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center"
                            onClick={() => {
                              // Download BOL file
                              const token = localStorage.getItem('token');
                              const bolFileName = selectedTruck.bol_file_path.split('/').pop();
                              
                              fetch(`/api/outbound-trucks/${selectedTruck.id}/download-bol`, {
                                headers: {
                                  'Authorization': `Bearer ${token}`
                                }
                              })
                              .then(response => {
                                if (!response.ok) throw new Error('Failed to download BOL');
                                return response.blob();
                              })
                              .then(blob => {
                                // Create a download link
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = bolFileName || `BOL-${selectedTruck.id}.pdf`;
                                document.body.appendChild(a);
                                a.click();
                                window.URL.revokeObjectURL(url);
                                document.body.removeChild(a);
                              })
                              .catch(error => {
                                console.error('Error downloading BOL:', error);
                                alert('Failed to download BOL file');
                              });
                            }}
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </button>
                          <button 
                            className="text-sm px-3 py-1 bg-amber-600 text-white rounded hover:bg-amber-700 flex items-center"
                            onClick={() => openBolUploadModal(selectedTruck)}
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            Replace
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-amber-400">
                          <FileX className="w-5 h-5 mr-2" />
                          <span className="text-sm">No BOL uploaded yet</span>
                        </div>
                        <button 
                          className="text-sm px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                          onClick={() => openBolUploadModal(selectedTruck)}
                        >
                          Upload BOL
                        </button>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Timeline */}
                <div className="bg-slate-800 rounded-md p-4">
                  <h4 className="font-medium text-slate-200 mb-3">Shipment Timeline</h4>
                  
                  <div className="relative pl-6 pb-2 border-l border-slate-600">
                    <div className="mb-4 relative">
                      <div className="absolute -left-[25px] bg-blue-600 rounded-full p-1">
                        <Calendar className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm font-medium text-slate-200">Scheduled</p>
                      <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.scheduled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    
                    <div className="mb-4 relative">
                      <div className="absolute -left-[25px] bg-green-600 rounded-full p-1">
                        <Truck className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm font-medium text-slate-200">Staged</p>
                      <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.staged_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    
                    <div className="mb-4 relative">
                      <div className="absolute -left-[25px] bg-purple-600 rounded-full p-1">
                        <Package className="w-3 h-3 text-white" />
                      </div>
                      <p className="text-sm font-medium text-slate-200">Loaded</p>
                      <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.loaded_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                    </div>
                    
                    {/* Show completed status if available */}
                    {selectedTruck.completed_at && (
                      <div className="relative">
                        <div className="absolute -left-[25px] bg-green-700 rounded-full p-1">
                          <CheckCircle className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-slate-200">Completed</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.completed_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}

                    {/* Show cancellation in timeline if cancelled */}
                    {(selectedTruck.cancelled_at || selectedTruck.status === 'Cancelled') && (
                      <div className="relative">
                        <div className="absolute -left-[25px] bg-red-700 rounded-full p-1">
                          <X className="w-3 h-3 text-white" />
                        </div>
                        <p className="text-sm font-medium text-red-200">Cancelled</p>
                        <p className="text-xs text-slate-400">{formatDateTime(selectedTruck.cancelled_at, 'MMM dd, yyyy h:mm a', userTimezone)}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Actions - fixed at bottom */}
              <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-600">
                <button
                  className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => setShowRecordModal(true)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View/Export Record
                </button>
                {openTruckEditor && (
                  <button 
                    className="flex items-center px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700"
                    onClick={() => openTruckEditor(selectedTruck)}
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Record
                  </button>
                )}
                {/* Upload BOL button - only show for completed trucks without BOL */}
                {selectedTruck.status === 'Completed' && !selectedTruck.bol_file_path && (
                  <button
                    className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                    onClick={() => openBolUploadModal(selectedTruck)}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload BOL
                  </button>
                )}
              </div>
              {/* Logiwa push state — renders only if a join row exists */}
              <LogiwaPushSection truckId={selectedTruck.id} />
            </div>
          </div>
        )}
      </div>
      
      {/* BOL Upload Modal */}
      <BolUploadModal
        isOpen={showBolUploadModal}
        onClose={() => {
          setShowBolUploadModal(false);
          setBolUploadTruck(null);
        }}
        onUpload={handleBolUpload}
        truckId={bolUploadTruck?.id}
        truckInfo={bolUploadTruck}
      />
      
      {/* BOL View Modal */}
      <BolViewModal
        isOpen={showBolViewModal}
        onClose={() => {
          setShowBolViewModal(false);
          setBolViewTruck(null);
        }}
        truckId={bolViewTruck?.id}
        truckInfo={bolViewTruck}
      />

      {/* Record View/Export Modal */}
      {showRecordModal && selectedTruck && (
        <OutboundRecordViewModal
          isOpen={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          record={selectedTruck}
        />
      )}
    </div>
  );
};

export default HistoryView;